import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Award, 
  FileText, 
  BarChart3, 
  Download, 
  Calendar as CalendarIcon,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Building2,
  Users,
  TrendingUp
} from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subYears } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { toast } from "sonner";
import * as XLSX from 'xlsx';

interface DrillDownData {
  id: string;
  name: string;
  count: number;
  details?: DrillDownData[];
}

interface AnalyticsData {
  badges: DrillDownData[];
  lessonPlans: DrillDownData[];
  surveys: DrillDownData[];
  totalBadges: number;
  totalLessonPlans: number;
  totalSurveys: number;
}

interface School {
  name: string;
  count: number;
}

type ViewLevel = 'district' | 'schools' | 'users';
type ReportType = 'badges' | 'lesson_plans' | 'surveys';
type TimeRange = 'this_month' | 'last_month' | 'this_quarter' | 'this_year' | 'last_year' | 'custom';

export function ComprehensiveAnalytics() {
  const { user, userProfile } = useAuth();
  const { isAdmin } = useRole();
  const [viewLevel, setViewLevel] = useState<ViewLevel>('district');
  const [activeReport, setActiveReport] = useState<ReportType>('badges');
  const [timeRange, setTimeRange] = useState<TimeRange>('this_month');
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined
  });
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [availableSchools, setAvailableSchools] = useState<School[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);

  const getDateRange = () => {
    const now = new Date();
    switch (timeRange) {
      case 'this_month':
        return { from: startOfMonth(now), to: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
      case 'this_quarter':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const quarterEnd = new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0);
        return { from: quarterStart, to: quarterEnd };
      case 'this_year':
        return { from: startOfYear(now), to: endOfYear(now) };
      case 'last_year':
        const lastYear = subYears(now, 1);
        return { from: startOfYear(lastYear), to: endOfYear(lastYear) };
      case 'custom':
        return { from: customDateRange.from || startOfMonth(now), to: customDateRange.to || endOfMonth(now) };
      default:
        return { from: startOfMonth(now), to: endOfMonth(now) };
    }
  };

  const loadAvailableSchools = async () => {
    if (!user || !userProfile) return;
    
    try {
      let query = supabase
        .from('profiles')
        .select('school_name')
        .not('school_name', 'is', null)
        .not('school_name', 'eq', '');

      // Filter by district if not admin
      if (!isAdmin && userProfile.district_id) {
        query = query.eq('district_id', userProfile.district_id);
      }

      const { data, error } = await query;
      
      if (error) throw error;

      // Count users per school and create school list
      const schoolCounts = data.reduce((acc: Record<string, number>, profile) => {
        const schoolName = profile.school_name;
        if (schoolName) {
          acc[schoolName] = (acc[schoolName] || 0) + 1;
        }
        return acc;
      }, {});

      const schools = Object.entries(schoolCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setAvailableSchools(schools);
    } catch (error) {
      console.error('Error loading schools:', error);
    }
  };

  const loadAnalyticsData = async () => {
    if (!user || !userProfile) return;
    
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      
      // Load badges data
      let badgesQuery = supabase
        .from('issued_certificates')
        .select(`
          id,
          created_at,
          user_id,
          badge_id,
          badges(name),
          profiles!user_id(first_name, last_name, school_name, district_id)
        `)
        .not('badge_id', 'is', null)
        .gte('created_at', from.toISOString())
        .lte('created_at', to.toISOString());

      // Load lesson plans data
      let lessonPlansQuery = supabase
        .from('lesson_plans')
        .select(`
          id,
          created_at,
          user_id,
          title,
          profiles!user_id(first_name, last_name, school_name, district_id)
        `)
        .gte('created_at', from.toISOString())
        .lte('created_at', to.toISOString());

      // Load surveys data - check if table exists, otherwise use mock data
      let surveysData: any[] = [];
      try {
        const surveysResult = await supabase
          .from('survey_responses')
          .select(`
            id,
            created_at,
            user_id,
            survey_id,
            surveys(title),
            profiles!user_id(first_name, last_name, school_name, district_id)
          `)
          .gte('created_at', from.toISOString())
          .lte('created_at', to.toISOString());
        
        if (!surveysResult.error) {
          surveysData = surveysResult.data || [];
        }
      } catch (error) {
        console.log('Survey responses table not found, using empty data');
        surveysData = [];
      }

      // Filter by district if not admin
      if (!isAdmin && userProfile.district_id) {
        badgesQuery = badgesQuery.eq('profiles.district_id', userProfile.district_id);
        lessonPlansQuery = lessonPlansQuery.eq('profiles.district_id', userProfile.district_id);
      }

      // Filter by selected schools if any are selected
      if (selectedSchools.length > 0) {
        badgesQuery = badgesQuery.in('profiles.school_name', selectedSchools);
        lessonPlansQuery = lessonPlansQuery.in('profiles.school_name', selectedSchools);
      }

      const [badgesResult, lessonPlansResult] = await Promise.all([
        badgesQuery,
        lessonPlansQuery
      ]);

      if (badgesResult.error) throw badgesResult.error;
      if (lessonPlansResult.error) throw lessonPlansResult.error;

      // Process data based on view level
      const processDataByLevel = (data: any[]) => {
        if (viewLevel === 'district') {
          return [{
            id: 'district',
            name: 'District Total',
            count: data.length,
            details: processSchoolLevel(data)
          }];
        } else if (viewLevel === 'schools') {
          return processSchoolLevel(data);
        } else {
          return processUserLevel(data);
        }
      };

      const processSchoolLevel = (data: any[]) => {
        const schoolGroups = data.reduce((acc, item) => {
          const schoolName = item.profiles?.school_name || 'Unknown School';
          if (!acc[schoolName]) {
            acc[schoolName] = [];
          }
          acc[schoolName].push(item);
          return acc;
        }, {});

        return Object.entries(schoolGroups).map(([schoolName, items]: [string, any[]]) => ({
          id: schoolName.toLowerCase().replace(/\s+/g, '-'),
          name: schoolName,
          count: items.length,
          details: items.map(item => ({
            id: item.user_id,
            name: `${item.profiles?.first_name || ''} ${item.profiles?.last_name || ''}`.trim(),
            count: 1
          }))
        }));
      };

      const processUserLevel = (data: any[]) => {
        const userGroups = data.reduce((acc, item) => {
          const userName = `${item.profiles?.first_name || ''} ${item.profiles?.last_name || ''}`.trim();
          const userId = item.user_id;
          const key = `${userId}-${userName}`;
          
          if (!acc[key]) {
            acc[key] = { userId, userName, count: 0 };
          }
          acc[key].count++;
          return acc;
        }, {});

        return Object.values(userGroups).map((user: any) => ({
          id: user.userId,
          name: user.userName || 'Unknown User',
          count: user.count
        }));
      };

      setAnalyticsData({
        badges: processDataByLevel(badgesResult.data || []),
        lessonPlans: processDataByLevel(lessonPlansResult.data || []),
        surveys: processDataByLevel(surveysData),
        totalBadges: badgesResult.data?.length || 0,
        totalLessonPlans: lessonPlansResult.data?.length || 0,
        totalSurveys: surveysData.length || 0
      });

    } catch (error) {
      console.error('Error loading analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvailableSchools();
  }, [user, userProfile]);

  useEffect(() => {
    loadAnalyticsData();
  }, [viewLevel, activeReport, timeRange, customDateRange, selectedSchools, user, userProfile]);

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const navigateToLevel = (level: ViewLevel) => {
    setViewLevel(level);
    setExpandedItems(new Set());
  };

  const exportData = async () => {
    try {
      const { from, to } = getDateRange();
      const currentData = getCurrentData();
      
      if (currentData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Flatten the data for Excel export
      const flattenData = (items: DrillDownData[], level: string = 'District') => {
        const flattened: any[] = [];
        
        items.forEach(item => {
          if (viewLevel === 'district' && item.details) {
            // For district level, show schools
            item.details.forEach(school => {
              if (school.details) {
                // Show users within each school
                school.details.forEach(user => {
                  flattened.push({
                    'District': 'District Total',
                    'School': school.name,
                    'User': user.name,
                    'Count': user.count
                  });
                });
              } else {
                flattened.push({
                  'District': 'District Total',
                  'School': school.name,
                  'User': '-',
                  'Count': school.count
                });
              }
            });
          } else if (viewLevel === 'schools') {
            if (item.details) {
              item.details.forEach(user => {
                flattened.push({
                  'School': item.name,
                  'User': user.name,
                  'Count': user.count
                });
              });
            } else {
              flattened.push({
                'School': item.name,
                'User': '-',
                'Count': item.count
              });
            }
          } else {
            // User level
            flattened.push({
              'User': item.name,
              'Count': item.count
            });
          }
        });
        
        return flattened;
      };

      const flatData = flattenData(currentData);
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(flatData);
      
      // Add summary row at the top
      const summaryData = [
        { 
          [Object.keys(flatData[0])[0]]: 'SUMMARY',
          [Object.keys(flatData[0])[1]]: '',
          [Object.keys(flatData[0])[Object.keys(flatData[0]).length - 2]]: 'Total Count',
          'Count': getCurrentTotal()
        },
        {}, // Empty row
        ...flatData
      ];
      
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      
      // Set column widths
      const colWidths = Object.keys(flatData[0]).map(() => ({ wch: 20 }));
      wsSummary['!cols'] = colWidths;
      
      // Add metadata sheet
      const metadata = [
        { 'Property': 'Report Type', 'Value': getReportTitle(activeReport) },
        { 'Property': 'View Level', 'Value': viewLevel.charAt(0).toUpperCase() + viewLevel.slice(1) },
        { 'Property': 'Time Range', 'Value': timeRange === 'custom' ? 'Custom Range' : timeRange.replace('_', ' ').toUpperCase() },
        { 'Property': 'Start Date', 'Value': format(from, 'PPP') },
        { 'Property': 'End Date', 'Value': format(to, 'PPP') },
        { 'Property': 'Generated At', 'Value': format(new Date(), 'PPP pp') },
        { 'Property': 'Generated By', 'Value': user?.email || 'Unknown' },
        { 'Property': 'Total Records', 'Value': getCurrentTotal() }
      ];
      
      const wsMetadata = XLSX.utils.json_to_sheet(metadata);
      wsMetadata['!cols'] = [{ wch: 15 }, { wch: 30 }];
      
      // Add sheets to workbook
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Data');
      XLSX.utils.book_append_sheet(wb, wsMetadata, 'Report Info');
      
      // Generate filename
      const reportName = activeReport.replace('_', '-');
      const filename = `edugogy-${reportName}-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      
      toast.success(`${getReportTitle(activeReport)} report exported successfully`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const handleUserClick = async (userId: string, userName: string) => {
    try {
      const { from, to } = getDateRange();
      
      // Fetch user activity based on active report type
      let activityData: any[] = [];
      
      if (activeReport === 'badges') {
        const { data } = await supabase
          .from('issued_certificates')
          .select('*, badges(name), created_at')
          .eq('user_id', userId)
          .gte('created_at', from.toISOString())
          .lte('created_at', to.toISOString());
        activityData = data || [];
      } else if (activeReport === 'lesson_plans') {
        const { data } = await supabase
          .from('lesson_plans')
          .select('*, created_at, title')
          .eq('user_id', userId)
          .gte('created_at', from.toISOString())
          .lte('created_at', to.toISOString());
        activityData = data || [];
      } else if (activeReport === 'surveys') {
        const { data } = await supabase
          .from('survey_responses')
          .select('*, surveys(title), created_at')
          .eq('user_id', userId)
          .gte('created_at', from.toISOString())
          .lte('created_at', to.toISOString());
        activityData = data || [];
      }

      // Show detailed activity in a toast or dialog
      const activitySummary = activityData.length > 0
        ? activityData.map(item => {
            const title = item.badges?.name || item.title || item.surveys?.title || 'Unknown';
            const date = format(new Date(item.created_at), 'PPP');
            return `${title} - ${date}`;
          }).join('\n')
        : 'No activity found in the selected date range';

      toast.info(`Activity for ${userName}`, {
        description: activitySummary,
        duration: 10000
      });
    } catch (error) {
      console.error('Error fetching user activity:', error);
      toast.error('Failed to load user activity');
    }
  };

  const renderDrillDownItem = (item: DrillDownData, level: number = 0) => {
    const hasDetails = item.details && item.details.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const isUser = level === 2 || (level === 0 && viewLevel === 'users');

    return (
      <div key={item.id} className="space-y-2">
        <div 
          className={cn(
            "flex items-center justify-between p-3 border rounded-lg transition-all",
            level > 0 && "ml-4 border-l-2 border-l-primary/20",
            (hasDetails || isUser) && "cursor-pointer hover:bg-muted/80 hover:shadow-sm",
            isUser && "hover:border-primary/50 group"
          )}
          onClick={() => {
            if (isUser) {
              handleUserClick(item.id, item.name);
            } else if (hasDetails) {
              toggleExpanded(item.id);
            }
          }}
          role={isUser || hasDetails ? "button" : undefined}
          tabIndex={isUser || hasDetails ? 0 : undefined}
        >
          <div className="flex items-center gap-2">
            {hasDetails && !isUser && (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            )}
            {level === 0 && viewLevel === 'district' && <Building2 className="h-4 w-4" />}
            {level === 1 && <Building2 className="h-4 w-4" />}
            {(level === 2 || isUser) && <Users className="h-4 w-4 text-primary" />}
            <span className={cn(
              "font-medium",
              isUser && "text-primary group-hover:underline"
            )}>
              {item.name}
            </span>
          </div>
          <Badge variant="secondary">{item.count}</Badge>
        </div>
        
        {hasDetails && isExpanded && !isUser && (
          <div className="space-y-1">
            {item.details.map(detail => renderDrillDownItem(detail, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const getCurrentData = () => {
    if (!analyticsData) return [];
    return analyticsData[activeReport] || [];
  };

  const getCurrentTotal = () => {
    if (!analyticsData) return 0;
    switch (activeReport) {
      case 'badges': return analyticsData.totalBadges;
      case 'lesson_plans': return analyticsData.totalLessonPlans;
      case 'surveys': return analyticsData.totalSurveys;
      default: return 0;
    }
  };

  const getReportIcon = (type: ReportType) => {
    switch (type) {
      case 'badges': return Award;
      case 'lesson_plans': return FileText;
      case 'surveys': return BarChart3;
    }
  };

  const getReportTitle = (type: ReportType) => {
    switch (type) {
      case 'badges': return 'Badges Earned';
      case 'lesson_plans': return 'Lesson Plans Created';
      case 'surveys': return 'Surveys Completed';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Comprehensive Analytics</h2>
          <p className="text-muted-foreground">
            Drill down through district, school, and user level data
          </p>
        </div>
        <Button onClick={exportData} disabled={loading}>
          <Download className="h-4 w-4 mr-2" />
          Export {getReportTitle(activeReport)} to Excel
        </Button>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Time Range */}
        <div>
          <Label>Time Range</Label>
          <Select value={timeRange} onValueChange={(value: TimeRange) => setTimeRange(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* School Selector */}
        <div>
          <Label>Schools ({selectedSchools.length} selected)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                {selectedSchools.length === 0 
                  ? "All Schools" 
                  : selectedSchools.length === 1 
                    ? selectedSchools[0]
                    : `${selectedSchools.length} schools selected`
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <div className="p-4 space-y-2 max-h-60 overflow-y-auto">
                <div className="flex items-center space-x-2 pb-2 border-b">
                  <Checkbox
                    id="select-all"
                    checked={selectedSchools.length === availableSchools.length && availableSchools.length > 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedSchools(availableSchools.map(school => school.name));
                      } else {
                        setSelectedSchools([]);
                      }
                    }}
                  />
                  <Label htmlFor="select-all" className="font-medium">
                    Select All ({availableSchools.length})
                  </Label>
                </div>
                {availableSchools.map((school) => (
                  <div key={school.name} className="flex items-center space-x-2">
                    <Checkbox
                      id={school.name}
                      checked={selectedSchools.includes(school.name)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedSchools([...selectedSchools, school.name]);
                        } else {
                          setSelectedSchools(selectedSchools.filter(s => s !== school.name));
                        }
                      }}
                    />
                    <Label htmlFor={school.name} className="flex-1 text-sm">
                      {school.name} ({school.count} users)
                    </Label>
                  </div>
                ))}
                {availableSchools.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No schools found
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Custom Date Range */}
        {timeRange === 'custom' && (
          <>
            <div>
              <Label>From Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customDateRange.from ? format(customDateRange.from, "PPP") : "Pick start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={customDateRange.from}
                    onSelect={(date) => setCustomDateRange(prev => ({ ...prev, from: date }))}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>To Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customDateRange.to ? format(customDateRange.to, "PPP") : "Pick end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={customDateRange.to}
                    onSelect={(date) => setCustomDateRange(prev => ({ ...prev, to: date }))}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}

        {/* View Level Toggle */}
        <div className="md:col-span-2">
          <Label>View Level</Label>
          <div className="flex border rounded-md">
            <Button
              variant={viewLevel === 'district' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => navigateToLevel('district')}
              className="flex-1"
            >
              District
            </Button>
            <Button
              variant={viewLevel === 'schools' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => navigateToLevel('schools')}
              className="flex-1"
            >
              Schools
            </Button>
            <Button
              variant={viewLevel === 'users' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => navigateToLevel('users')}
              className="flex-1"
            >
              Users
            </Button>
          </div>
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeReport} onValueChange={(value: ReportType) => setActiveReport(value)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="badges" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Badges
          </TabsTrigger>
          <TabsTrigger value="lesson_plans" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Lesson Plans
          </TabsTrigger>
          <TabsTrigger value="surveys" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Surveys
          </TabsTrigger>
        </TabsList>

        {['badges', 'lesson_plans', 'surveys'].map((reportType) => {
          const ReportIcon = getReportIcon(reportType as ReportType);
          
          return (
            <TabsContent key={reportType} value={reportType} className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ReportIcon className="h-5 w-5" />
                      <CardTitle>{getReportTitle(reportType as ReportType)}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-lg px-3 py-1">
                        Total: {getCurrentTotal()}
                      </Badge>
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <CardDescription>
                    Viewing {viewLevel} level data for {getReportTitle(reportType as ReportType).toLowerCase()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {getCurrentData().length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          No data available for the selected time range
                        </div>
                      ) : (
                        getCurrentData().map(item => renderDrillDownItem(item))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}